import { Fragment, ReactElement, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/auth';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { addBranches, setAlert } from '../../store/actionCreators';
import SweetAlert from 'react-bootstrap-sweetalert';
import {
  BellIcon,
  BookOpenIcon,
  ChevronDownIcon,
  KeyIcon,
  UserIcon,
  DocumentTextIcon,
  PlusIcon,
  ViewColumnsIcon,
  TableCellsIcon,
  DocumentChartBarIcon,
  MapPinIcon,
  XMarkIcon,
  Bars3Icon,
  BriefcaseIcon,
  ListBulletIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Loading from '../Common/Loading';
import { RootReducerState } from '../../store/reducer';
import { Branch } from '@deliveries-app/models';
import { bookingService, branchService } from '@deliveries-app/services';
import { darkenColor } from '../../utils/helpers';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import TranscriptionSidebar from '../Transcription/TranscriptionSidebar';

const navigation = [
  { name: 'Dashboard', href: '/', icon: ViewColumnsIcon, current: true },
  {
    name: 'All Bookings',
    href: '/bookings',
    icon: BookOpenIcon,
    current: false,
  },
  {
    name: 'Draft Bookings',
    href: '/draft-bookings',
    icon: ListBulletIcon,
    current: false,
  },
  {
    name: 'Quotes',
    href: '/quotes',
    icon: DocumentTextIcon,
    current: false,
  },
  { name: 'Vehicles', href: '/vehicles', icon: TableCellsIcon, current: false },
  { name: 'Users', href: '/users', icon: UserIcon, current: false },
  {
    name: 'Reporting',
    href: '/reporting',
    icon: DocumentChartBarIcon,
    current: false,
  },
  { name: 'Eroad API Keys', href: '/eroad', icon: KeyIcon, current: false },
  {
    name: 'Heatmap',
    href: '/heatmap',
    icon: MapPinIcon,
    current: false,
  },
  {
    name: 'Products',
    href: '/products',
    icon: BriefcaseIcon,
    current: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

interface Props {
  title?: string;
  children?: ReactElement | ReactElement[];
}

export default function Auth({ title, children, ...rest }: Props) {
  const router = useRouter();
  const dispatch = useDispatch();

  const { user, logout } = useAuth({ middleware: 'auth' });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [transcriptionOpen, setTranscriptionOpen] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const { flags } = useFeatureFlags();

  const selectedBranch = useSelector(
    (state: RootReducerState) => state.dashboard.selectedBranch
  );
  const alertType = useSelector(
    (state: RootReducerState) => state.dashboard.alert
  );

  const fetchBranches = async () => {
    try {
      const res = await branchService.getBranches();

      setBranches(res);

      if (selectedBranch == null) {
        dispatch(addBranches(res[0]));
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, [selectedBranch, user]);

  if (!selectedBranch) {
    return <Loading branchColour="#31E2C4" />;
  }

  return (
    <div className="">
      {alertType.type && (
        <div>
          <SweetAlert
            success
            title={alertType.title}
            show={alertType.type === 'success'}
            onConfirm={() =>
              dispatch(
                setAlert({
                  title: '',
                  type: null,
                  subTitle: '',
                })
              )
            }
            customButtons={
              <button
                className={`h-full rounded-md px-5 text-white`}
                style={{
                  backgroundColor: selectedBranch?.branch_colour,
                  transition: 'background-color 0.3s',
                }}
                onClick={() =>
                  dispatch(
                    setAlert({
                      title: '',
                      type: null,
                      subTitle: '',
                    })
                  )
                }
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    darkenColor(selectedBranch?.branch_colour, 10);
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    selectedBranch?.branch_colour;
                }}
              >
                Ok
              </button>
            }
          >
            {alertType.subTitle}
          </SweetAlert>
          <SweetAlert
            danger
            title={alertType.title}
            show={alertType.type === 'danger'}
            onConfirm={() =>
              dispatch(
                setAlert({
                  title: '',
                  type: null,
                  subTitle: '',
                })
              )
            }
            customButtons={
              <button
                className="  hover:bg-red-darker h-full rounded-md bg-red-600 px-5 text-white "
                onClick={() =>
                  dispatch(
                    setAlert({
                      title: '',
                      type: null,
                      subTitle: '',
                    })
                  )
                }
              >
                Ok
              </button>
            }
          >
            {alertType.subTitle}
          </SweetAlert>
        </div>
      )}

      {/* Dynamic sidebar for mobile */}
      <MobileNavigation
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Static sidebar for desktop */}
      <DesktopNavigation branches={branches} />

      <div className="flex h-screen flex-col md:pl-64">
        {/* Top page Header */}
        <Header
          setSidebarOpen={setSidebarOpen}
          logout={logout}
          selectedBranch={selectedBranch}
          user={user}
          showTranscription={flags['live-transcriptions']}
          transcriptionOpen={transcriptionOpen}
          onTranscriptionToggle={() => setTranscriptionOpen((prev) => !prev)}
        />

        <main className=" flex flex-1 flex-col py-6 pt-20">
          <div className="relative px-4 sm:px-6 md:px-8">
            {router.asPath === '/settings' ? (
              <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
            ) : router.asPath === '/quotes' ? (
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Quotes</h1>
                <Link href={'/bookings/create'}>
                  <a
                    className="flex items-center rounded-md py-2 px-5 text-xs text-white"
                    style={{
                      backgroundColor: selectedBranch?.branch_colour,
                      transition: 'background-color 0.3s',
                    }}
                    onMouseEnter={(e) => {
                      (
                        e.currentTarget as HTMLAnchorElement
                      ).style.backgroundColor = darkenColor(
                        selectedBranch?.branch_colour,
                        10
                      );
                    }}
                    onMouseLeave={(e) => {
                      (
                        e.currentTarget as HTMLAnchorElement
                      ).style.backgroundColor = selectedBranch?.branch_colour;
                    }}
                  >
                    <PlusIcon width={20} height={20} className="mr-[5px]" />{' '}
                    Create Quote
                  </a>
                </Link>
              </div>
            ) : (
              navigation.map(({ name, href }, id) => (
                <h1 className="text-2xl font-semibold text-gray-900" key={id}>
                  {router.asPath === href && name}
                </h1>
              ))
            )}
          </div>

          <div className="relative mx-auto  w-full flex-1">{children}</div>
        </main>
      </div>

      {/* Live Transcription Sidebar */}
      {flags['live-transcriptions'] && (
        <TranscriptionSidebar
          open={transcriptionOpen}
          onClose={() => setTranscriptionOpen(false)}
        />
      )}
    </div>
  );
}

const MobileNavigation = ({ sidebarOpen, setSidebarOpen }) => {
  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-40 flex md:hidden"
        onClose={setSidebarOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="transition ease-in-out duration-300 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
        >
          <div className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-800 pt-5 pb-4">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in-out duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon
                    className="h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </Transition.Child>
            <div className="flex flex-shrink-0 items-center px-4">
              <Image
                src="https://tailwindui.com/img/logos/workflow-logo-primary-mark-white-text.svg"
                alt="Workflow"
                width={500}
                height={500}
              />
            </div>
            <div className="mt-5 h-0 flex-1 overflow-y-auto">
              <nav className="space-y-1 px-2">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                      'group flex items-center rounded-md px-2 py-2 text-base font-medium'
                    )}
                  >
                    <item.icon
                      className={classNames(
                        item.current
                          ? 'text-gray-300'
                          : 'text-gray-400 group-hover:text-gray-300',
                        'mr-4 h-6 w-6 flex-shrink-0'
                      )}
                      aria-hidden="true"
                    />
                    {item.name}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </Transition.Child>
        <div className="w-14 flex-shrink-0" aria-hidden="true">
          {/* Dummy element to force sidebar to shrink to fit close icon */}
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const DesktopNavigation = ({ branches }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const selectedBranch = useSelector(
    (state: RootReducerState) => state.dashboard.selectedBranch
  );

  const [draftbookingCount, setDraftBookingCount] = useState<number>(0);

  const fetchDraftBookings = async () => {
    try {
      if(selectedBranch && selectedBranch.id) {
        const res = await bookingService.getDraftBookings(selectedBranch.id, {
          page: 1,
          perPage: 25,
          order: 'id,desc',
        });

        if(res._metadata && res._metadata.total) {
          setDraftBookingCount(res._metadata.total);
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    fetchDraftBookings();
  }, []);

  return (
    <div className="z-50 hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
      {/* Sidebar component, swap this element with another sidebar if you like */}
      <div className="flex min-h-0 flex-1 flex-col bg-gray-800">
        <div className="flex h-16 flex-shrink-0 items-center bg-gray-900 px-4">
          <Image
            src={selectedBranch?.logo_url}
            alt="Branch Logo"
            objectFit="contain"
            width={200}
            height={80}
            className="h-auto max-h-[80px]"
          />
        </div>
        {/* Branch dropdown */}
        <Menu as="div" className="relative mt-4 px-2">
          <div>
            <Menu.Button
              className="flex w-full rounded-md bg-gray-900 py-2 text-sm focus:outline-none"
              style={{
                transition: 'box-shadow 0.3s',
              }}
              onFocus={(e) => {
                (
                  e.currentTarget as HTMLElement
                ).style.boxShadow = `0 0 0 2px ${selectedBranch?.branch_colour}`;
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              <span className="sr-only">Open branch selection menu</span>
              <div className="flex w-full justify-between px-4 text-base text-white">
                {selectedBranch?.name ?? 'Select branch'}
                <ChevronDownIcon className="h-6" />
              </div>
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute mt-2 w-60 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {branches.map((item) => (
                <Menu.Item key={item.name}>
                  {({ active }) => (
                    <button
                      onClick={() => dispatch(addBranches(item))}
                      type="button"
                      className={classNames(
                        active ? 'bg-gray-100' : '',
                        'block w-full px-4 py-2 text-left text-sm text-gray-700'
                      )}
                    >
                      {item.name}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
        <div className="flex flex-1 flex-col overflow-y-auto">
          <nav className="flex-1 space-y-1 py-4">
            {navigation.map((item) => (
              <Link href={item.href} key={item.name} passHref>
                <a
                  className={classNames(
                    router.pathname == item.href
                      ? `border-l-2 bg-gray-900 text-white`
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                    'group flex items-center px-2 py-3 font-medium',
                    {
                      border: router.pathname == item.href,
                    }
                  )}
                  style={
                    router.pathname == item.href
                      ? { borderLeftColor: selectedBranch?.branch_colour }
                      : undefined
                  }
                >
                  <item.icon
                    className={classNames(
                      router.pathname == item.href
                        ? 'text-gray-300'
                        : 'text-gray-400 group-hover:text-gray-300',
                      'mr-3 h-6 w-6 flex-shrink-0'
                    )}
                    aria-hidden="true"
                  />
                  <span>{item.name}</span>
                  {item.href == '/draft-bookings' && (
                    <span className='ml-auto w-6 h-6 text-black flex items-center justify-center text-sm rounded-full' style={{
                      backgroundColor: selectedBranch?.branch_colour
                    }}>{draftbookingCount}</span>
                  )} 
                </a>
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
};

const Header = ({
  setSidebarOpen,
  logout,
  selectedBranch,
  user,
  showTranscription,
  transcriptionOpen,
  onTranscriptionToggle,
}) => {
  const router = useRouter();

  const userNavigation = [
    { name: 'Settings', click: () => router.push('/settings') },
    { name: 'Sign out', click: () => logout() },
  ];

  return (
    <div className="fixed top-0 z-40 flex h-16 w-full flex-shrink-0 bg-white pr-64 shadow">
      <button
        type="button"
        className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none md:hidden"
        style={{
          transition: 'box-shadow 0.3s',
        }}
        onClick={() => setSidebarOpen(true)}
        onFocus={(e) => {
          (
            e.currentTarget as HTMLButtonElement
          ).style.boxShadow = `0 0 0 2px ${selectedBranch?.branch_colour}`;
        }}
        onBlur={(e) => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
        }}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>
      <div className="flex flex-1 justify-between px-4">
        <div className="flex h-full items-center">
          {router.asPath === '/quotes' ? (
            <Link href={'/bookings/create'}>
              <a
                className="flex items-center rounded-md py-2 px-5 text-white"
                style={{
                  backgroundColor: selectedBranch?.branch_colour,
                  transition: 'background-color 0.3s',
                }}
              >
                <PlusIcon width={20} height={20} className="mr-[5px]" /> Create
                a new order
              </a>
            </Link>
          ) : (
            <h2 className="text-xl font-bold">
              Branch: {selectedBranch?.name}
            </h2>
          )}
        </div>
        <div className="ml-4 flex items-center md:ml-6">
          <button
            type="button"
            className="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              transition: 'box-shadow 0.3s',
            }}
            onFocus={(e) => {
              (
                e.currentTarget as HTMLButtonElement
              ).style.boxShadow = `0 0 0 2px ${selectedBranch?.branch_colour}`;
            }}
            onBlur={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            <span className="sr-only">View notifications</span>
            <BellIcon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Live Transcription toggle */}
          {showTranscription && (
            <button
              type="button"
              onClick={onTranscriptionToggle}
              className={`relative rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                transcriptionOpen
                  ? 'bg-teal-500 text-white'
                  : 'bg-white text-gray-400 hover:text-gray-500'
              }`}
              style={{
                transition: 'background-color 0.2s, color 0.2s',
              }}
            >
              <span className="sr-only">Toggle live transcription</span>
              <MicrophoneIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          )}

          {/* Profile dropdown */}
          <Menu as="div" className="relative ml-3">
            <div>
              <Menu.Button
                className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  transition: 'box-shadow 0.3s',
                }}
                onFocus={(e) => {
                  (
                    e.currentTarget as HTMLButtonElement
                  ).style.boxShadow = `0 0 0 2px ${selectedBranch?.branch_colour}`;
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    'none';
                }}
              >
                <span className="sr-only">Open user menu</span>
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full font-medium text-white"
                  style={{
                    backgroundColor: selectedBranch?.branch_colour,
                  }}
                >
                  {user?.data.name[0]}
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {userNavigation.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={item.click}
                        className={classNames(
                          active ? 'bg-gray-100' : '',
                          'block w-full px-4 py-2 text-left text-sm text-gray-700'
                        )}
                      >
                        {item.name}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};
